// 施工の正規化応答(Constructions + related バケット)を、従来のネスト形(constructionRow:
// 施工→ConstructionPlan→Work、施工→ConstructionRecords→User、施工→Materials、
// 施工→User)へ復元する共通ハイドレータ。結合は nestHydrator の宣言的エンジン
// (生成メタデータのエッジキー)で行い、自動生成できない業務射影(消費材料の重複排除)だけ関数で残す。
// related.ConstructionPlans / Works / ConstructionRecords はサーバー側で alive のみ
// (従来 include の where deleted_at:null を踏襲)のため、削除済みの参照先は従来どおり null になる。
// 工事(Work)は担当者を持たないため User ネストは無い。
// User の結び付けは api 側 Sequelize の関連付けと同じ規則:
//   施工 = created_by_user_id(同名 belongsTo の後勝ち)、施工計画/施工記録 = preparer_user_id。
import { constructionRow, constructionTreeRow, NormalizedConstructionResponse, NormalizedConstructionTreeResponse } from "../../schema/console/construction";
import { MaterialBase } from "../../schema/generated/Material";
import { belongsToOf, hasManyOf, mapNullable, requiredUserOf } from "./nestHydrator";
import { mapById, usersWithGroupOf } from "./relatedLookup";

// 【自動生成できない業務射影】施工ごとの消費材料QR個票を消費行から解決する
// (api 旧 attachConsumedMaterials と同じ規則)。
// 分割消費(同一施工・同一QRがバッチ/工程違いで複数消費行を持つ)でも Materials に同じ材料QRが
// 重複しないよう、施工×材料QRの組で1件だけ付与する(消費行の並び順を保つ)。
function consumedMaterialsByConstruction(consumptions: NormalizedConstructionResponse["related"]["ConstructionMaterialConsumptions"], materials: MaterialBase[]): Map<string, MaterialBase[]> {
  const materialById = mapById(materials);
  const byConstruction = new Map<string, MaterialBase[]>();
  const attachedQrByConstruction = new Set<string>();
  for (const consumption of consumptions) {
    const material = materialById.get(consumption.material_qr_id);
    const dedupeKey = `${consumption.construction_id}:${consumption.material_qr_id}`;
    const alreadyAttached = attachedQrByConstruction.has(dedupeKey);
    if (material != null && !alreadyAttached) {
      attachedQrByConstruction.add(dedupeKey);
      const list = byConstruction.get(consumption.construction_id) ?? [];
      list.push(material);
      byConstruction.set(consumption.construction_id, list);
    }
  }
  return byConstruction;
}

export function hydrateConstructions(normalized: NormalizedConstructionResponse): constructionRow[] {
  const related = normalized.related;
  const users = usersWithGroupOf(related.Users, related.Groups);
  const constructionPlanOf = belongsToOf("Construction.construction_plan_id", related.ConstructionPlans);
  const workOf = belongsToOf("ConstructionPlan.work_id", related.Works);
  const recordsOf = hasManyOf("Construction.ConstructionRecords", related.ConstructionRecords);
  const constructionUserOf = requiredUserOf("Construction.created_by_user_id", users, "施工", "作成者");
  const planUserOf = requiredUserOf("ConstructionPlan.preparer_user_id", users, "施工計画", "担当");
  const recordUserOf = requiredUserOf("ConstructionRecord.preparer_user_id", users, "施工記録", "担当");
  const materialsByConstructionId = consumedMaterialsByConstruction(related.ConstructionMaterialConsumptions, related.Materials);
  return normalized.Constructions.map((construction): constructionRow => ({
    ...construction,
    ConstructionPlan: mapNullable(constructionPlanOf(construction), (plan) => ({
      ...plan,
      Work: workOf(plan),
      User: planUserOf(plan),
    })),
    User: constructionUserOf(construction),
    Materials: materialsByConstructionId.get(construction.id) ?? [],
    ConstructionRecords: recordsOf(construction.id).map((record) => ({ ...record, User: recordUserOf(record) })),
  }));
}

// 施工ツリーの正規化応答を従来のネスト形(constructionTreeRow: 5兄弟の1段配列ネスト)へ復元する。
// related の各バケットはサーバー側で alive のみ(従来 include の where deleted_at:null を踏襲)。
export function hydrateConstructionTrees(normalized: NormalizedConstructionTreeResponse): constructionTreeRow[] {
  const related = normalized.related;
  const constructionActionsOf = hasManyOf("Construction.ConstructionActions", related.ConstructionActions);
  const consumptionsOf = hasManyOf("Construction.ConstructionMaterialConsumptions", related.ConstructionMaterialConsumptions);
  const processExecutionsOf = hasManyOf("Construction.ProcessExecutions", related.ProcessExecutions);
  const qualityRecordsOf = hasManyOf("Construction.QualityRecords", related.QualityRecords);
  const stirBatchesOf = hasManyOf("Construction.StirBatchs", related.StirBatches);
  return normalized.Constructions.map((construction): constructionTreeRow => ({
    ...construction,
    ConstructionActions: constructionActionsOf(construction.id),
    ConstructionMaterialConsumptions: consumptionsOf(construction.id),
    ProcessExecutions: processExecutionsOf(construction.id),
    QualityRecords: qualityRecordsOf(construction.id),
    StirBatches: stirBatchesOf(construction.id),
  }));
}
